<template>
  <section class="modal-ai-setup-config-section">
    <div class="modal-ai-setup-config-section__row">
      <DropdownBordered
        id="ai-setup-provider"
        track="provider"
        labelName="providerTitle"
        :value="selectedProvider"
        :options="providers"
        :tooltip="false"
        :multiple="false"
        :closeOnSelect="true"
        :hideSelected="true"
        :disabledMultiselect="providers.length <= 1 && !!selectedProvider"
        placeholder="Select AI Provider"
        @input="onSelectProvider"
      >
        <template #label>
          <BaseSubHeading
            size="large"
            weight="400"
            color="monochrome-06"
          >
            AI Provider
          </BaseSubHeading>
        </template>
      </DropdownBordered>

      <DropdownBordered
        id="ai-setup-model"
        track="model"
        labelName="title"
        :value="selectedModel"
        :options="models"
        :tooltip="false"
        :multiple="false"
        :closeOnSelect="true"
        :hideSelected="true"
        :disabledMultiselect="isModelSelectDisabled"
        placeholder="Select LLM version"
        @input="onSelectModel"
      >
        <template #label>
          <BaseSubHeading
            size="large"
            weight="400"
            color="monochrome-06"
          >
            LLM version
          </BaseSubHeading>
        </template>
      </DropdownBordered>
    </div>

    <BorderedTextarea
      :modelValue="apiKey"
      :resize="false"
      :customErrorMessage="apiKeyErrorMessage"
      maxlength="auto"
      placeholder="Enter API Key"
      @update:modelValue="onApiKeyChange"
    >
      <template #label>
        <BaseSubHeading
          size="large"
          weight="400"
          color="monochrome-06"
        >
          API Key
        </BaseSubHeading>
      </template>
    </BorderedTextarea>
  </section>
</template>

<script setup lang="ts">
import { computed } from "vue"

import BaseSubHeading from "@/components/Base/BaseSubHeading.vue"
import DropdownBordered from "@/components/Dropdown/DropdownBordered.vue"
import BorderedTextarea from "@/components/Form/BorderedTextarea.vue"

import { AIConfigModels, AIConfigProvider } from "@/models"

const props = defineProps({
  providers: {
    type: Array as () => AIConfigProvider[],
    default: () => []
  },
  models: {
    type: Array as () => AIConfigModels[],
    default: () => []
  },
  selectedProvider: {
    type: Object as () => AIConfigProvider | null,
    default: null
  },
  selectedModel: {
    type: Object as () => AIConfigModels | null,
    default: null
  },
  apiKey: {
    type: String,
    default: ""
  },
  apiKeyErrorMessage: {
    type: String,
    default: ""
  }
})

const isModelSelectDisabled = computed(() =>
  AIConfigModels.isSelectDisabled(props.models, props.selectedModel)
)

const emit = defineEmits<{
  (e: "update:provider", value: AIConfigProvider): void
  (e: "update:model", value: AIConfigModels): void
  (e: "update:apiKey", value: string): void
}>()

const onSelectProvider = ({ value }: { value: AIConfigProvider }) => {
  emit("update:provider", value)
}

const onSelectModel = ({ value }: { value: AIConfigModels }) => {
  emit("update:model", value)
}

const onApiKeyChange = ({ value }: { value: string }) => {
  emit("update:apiKey", value)
}
</script>

<style scoped lang="scss">
@import "@/assets/scss/utils/mixins/_flex";

.modal-ai-setup-config-section {
  @include flex($direction: column, $align: unset);

  width: 624px;
  padding-top: var(--step12);
  gap: var(--step4);

  @media (max-width: 1536px) {
    width: 488px;
    padding-top: var(--step10);
  }

  @media (max-width: 1280px) {
    width: 448px;
  }

  &__row {
    @include flex($justify: space-between, $align: unset);

    gap: var(--step4);
  }
}
</style>
